import { useNavigation } from "@/providers";
import {
  RobotoStyledPopoverMenu as Menu,
  RobotoMenuItem as MenuItem,
  RobotoLinkButton as Link,
  RobotoLink,
} from "@/shared/components";
import { SessionRecord } from "@/shared/domain/sessions";
import { usePopupState } from "@/shared/hooks";
import { Goto } from "@/shared/navigation";

import styles from "./SessionInfo.module.css";

interface SessionInfoProps {
  sessions: SessionRecord[];
}

/** An unnamed session is labeled by its id. */
function sessionLabel(session: SessionRecord): string {
  return session.name ?? session.session_id;
}

/**
 * The breadcrumb's session segment when the workspace was opened from sessions:
 * a single session's name, or a count that opens a menu of sessions. Mirrors
 * {@link DatasetInfo}.
 */
export function SessionInfo({ sessions }: SessionInfoProps) {
  if (sessions.length === 1) {
    return <SingleSessionInfo session={sessions[0]} />;
  }

  if (sessions.length > 1) {
    return <MultipleSessionsInfo sessions={sessions} />;
  }

  return null;
}

function SingleSessionInfo({ session }: { session: SessionRecord }) {
  const displayText = sessionLabel(session);

  return (
    <div className={styles.sessionInfoContainer}>
      <RobotoLink
        className={styles.sessionInfo}
        title={displayText}
        to={Goto.createSessionRoute(session.session_id)}
        underline="hover"
      >
        {displayText}
      </RobotoLink>
    </div>
  );
}

function MultipleSessionsInfo({ sessions }: { sessions: SessionRecord[] }) {
  const popup = usePopupState();

  return (
    <>
      <Menu anchorEl={popup.anchor} open={popup.isOpen} onClose={popup.close}>
        {sessions.map((session) => (
          <SessionMenuItem key={session.session_id} session={session} />
        ))}
      </Menu>
      <Link
        component="button"
        underline="hover"
        color="inherit"
        className={styles.linkButton}
        onClick={(event) => popup.open({ anchorEl: event.currentTarget })}
        title={sessions.map(sessionLabel).join(", ")}
      >
        {sessions.length} Sessions
      </Link>
    </>
  );
}

function SessionMenuItem({ session }: { session: SessionRecord }) {
  const { goto } = useNavigation();

  return (
    <MenuItem
      eventName="SessionOpened"
      eventProperties={{ sessionId: session.session_id }}
      onClick={() => goto.session({ sessionId: session.session_id })}
    >
      {sessionLabel(session)}
    </MenuItem>
  );
}
